import { ComponentPropsWithoutRef, useState, useEffect } from "react";

import { Spinner } from "components/Loading";

export function SyncedTextInput(
  props: {
    syncedVal: string | null;
    onAnyChange?: (val: string) => void;
    onUpdate: (val: string) => void;
  } & ComponentPropsWithoutRef<"input">
) {
  const { syncedVal, onAnyChange, onUpdate, ...extraProps } = props;
  const [val, setVal] = useState("");

  useEffect(() => {
    if (syncedVal === null) return;
    // Keep the input in sync if someone else changes it.
    setVal(syncedVal);
  }, [syncedVal, setVal]);

  if (syncedVal === null) return <Spinner inline={true} />;

  return (
    <input
      type="text"
      value={val}
      onChange={(e) => {
        setVal(e.target.value);
        onAnyChange?.(e.target.value);
        // It's possible for the text input value to be updated
        // without being in focus, such as by pressing the up/down
        // buttons in a number input.
        // In such cases, fire onUpdate immediately.
        // If the text input is in focus, then the user might not
        // be done typing the value, so wait until onBlur to fire
        // the event.
        if (document.activeElement !== e.target) onUpdate(e.target.value);
      }}
      onBlur={(e) => {
        if (val !== syncedVal) {
          onUpdate(val);
        }
      }}
      {...extraProps}
    />
  );
}

export function SyncedNumberInput(
  props: {
    syncedVal: number | null;
    onAnyChange?: (val: number) => void;
    onUpdate: (val: number) => void;
  } & ComponentPropsWithoutRef<"input">
) {
  const { syncedVal, onAnyChange, onUpdate, ...extraProps } = props;
  return (
    <SyncedTextInput
      type="number"
      syncedVal={syncedVal === null ? null : syncedVal.toString()}
      onAnyChange={
        onAnyChange === undefined
          ? undefined
          : (val) => onAnyChange(Number(val))
      }
      onUpdate={(val) => onUpdate(Number(val))}
      {...extraProps}
    />
  );
}

export function SyncedTextArea(
  props: {
    syncedVal: string | null;
    onUpdate: (val: string) => void;
  } & ComponentPropsWithoutRef<"textarea">
) {
  const { syncedVal, onUpdate, ...extraProps } = props;
  const [val, setVal] = useState("");

  // TODO: maybe sync it periodically instead of only on blur.

  useEffect(() => {
    if (syncedVal === null) return;
    // Keep the input in sync if someone else changes it.
    setVal(syncedVal);
  }, [syncedVal, setVal]);

  if (syncedVal === null) return <Spinner inline={true} />;

  return (
    <textarea
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={(e) => {
        if (val !== syncedVal) {
          onUpdate(val);
        }
      }}
      {...extraProps}
    />
  );
}
